import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Spinner, Alert } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaEye, FaFileInvoice, FaRedo } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const InvoiceList = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [refresh, setRefresh] = useState(0);

  // Status class function
  const getStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "pending") return "status-badge status-pending";
    if (s === "paid" || s === "approved" || s === "granted" || s === "accepted")
      return "status-badge status-approved";
    if (s === "overdue" || s === "rejected" || s === "declined")
      return "status-badge status-rejected";
    if (s === "in progress" || s === "in-progress")
      return "status-badge status-progress";
    if (s === "cancelled" || s === "cancel")
      return "status-badge status-cancelled";
    if (s === "completed") return "status-badge status-completed";
    if (s === "generated") return "status-badge status-info";
    return "status-badge status-default";
  };

  // Fetch invoices from API
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.post("/invoice_list.php", {});
      
      if (response.data.head.code === 200) {
        setInvoices(response.data.body);
      } else {
        setError(response.data.head.msg || "Failed to fetch invoices");
        setInvoices([]);
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setError("Failed to load invoices. Please try again.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [refresh]);

  // Delete invoice handler
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        const response = await api.post("/invoice_delete.php", { id });
        
        if (response.data.head.code === 200) {
          alert("Invoice deleted successfully!");
          fetchInvoices(); // Refresh the list
        } else {
          alert("Failed to delete invoice: " + response.data.head.msg);
        }
      } catch (err) {
        console.error("Error deleting invoice:", err);
        alert("Failed to delete invoice. Please try again.");
      }
    }
  };

   const parseYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;

  const [day, month, year] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

  // Filter invoices based on search, date range, and status
  const filteredInvoices = invoices.filter((invoice) => {
    if (!search && !fromDate && !toDate && !statusFilter) return true;
    
    const query = search.toLowerCase();
    const matchesSearch = 
      String(invoice.id).toLowerCase().includes(query) ||
      (invoice.invoice_no && invoice.invoice_no.toLowerCase().includes(query)) ||
      (invoice.customer_name && invoice.customer_name.toLowerCase().includes(query)) ||
      (invoice.customer_phone && invoice.customer_phone.includes(query)) ||
      (invoice.grand_total && invoice.grand_total.toString().includes(query));

    // Date range filtering
    // const invoiceDate = new Date(invoice.date);
    // const from = fromDate ? new Date(fromDate) : null;
    // const to = toDate ? new Date(toDate) : null;
    const invoiceDate = parseDDMMYYYY(invoice.date);
    const from = fromDate ? parseYYYYMMDD(fromDate) : null;
    const to = toDate ? parseYYYYMMDD(toDate) : null;

    const matchesDateRange = 
      (!from || invoiceDate >= from) && 
      (!to || invoiceDate <= to);

    // Status filtering
    const matchesStatus = !statusFilter || invoice.status === statusFilter;

    return matchesSearch && matchesDateRange && matchesStatus;
  });

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentInvoices,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredInvoices, 10);

  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
  };

  const clearStatusFilter = () => {
    setStatusFilter("");
  };

  const clearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSearch("");
  };

  const refreshList = () => {
    setRefresh(prev => prev + 1);
  };

  // Calculate stats
  const paidCount = invoices.filter(i => i.status === 'Paid').length;
  const pendingCount = invoices.filter(i => i.status === 'Pending').length;
  const overdueCount = invoices.filter(i => i.status === 'Overdue').length;
  const totalAmount = filteredInvoices.reduce((total, invoice) => 
    total + (parseFloat(invoice.grand_total) || 0), 0);

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Invoice Management</h2>
      
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
          <Button variant="outline-danger" size="sm" className="ms-3" onClick={refreshList}>
            <FaRedo /> Retry
          </Button>
        </Alert>
      )}

      {/* Filters Section */}
      <div className="filters-section mb-4 p-3 border rounded bg-light">
        <Row className="g-3">
          {/* Date Range Filters */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>From Date:</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>To Date:</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          
          {/* Status Filter 
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>Status:</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Generated">Generated</option>
                <option value="Paid">Paid</option>
                <option value="Pending">Pending</option>
                <option value="Overdue">Overdue</option>
                <option value="Cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
          </Col>
          */}

          {/* Filter Actions */}
          <Col xs={12} md={6} lg={3} className="d-flex align-items-end gap-2">
            <Button 
              variant="outline-secondary" 
              onClick={clearDateFilter}
              disabled={!fromDate && !toDate}
              size="sm"
            >
              Clear Dates
            </Button>
            {/* <Button 
              variant="outline-secondary" 
              onClick={clearAllFilters}
              disabled={!fromDate && !toDate && !statusFilter && !search}
              size="sm"
            >
              Clear All
            </Button>
            <Button 
              variant="outline-primary" 
              onClick={refreshList}
              size="sm"
              title="Refresh"
            >
              <FaRedo />
            </Button> */}
          </Col>
        </Row>
      </div>

      {/* Search and Add Invoice Section */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
          <Form.Control
            placeholder="Search ID, customer name, amount"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={loading}
          />
        </Col>

        <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end gap-2">
          {/* <Button 
            variant="outline-secondary" 
            onClick={refreshList}
            disabled={loading}
          >
            <FaRedo /> Refresh
          </Button> */}
          <Link
            to="/addinvoice"
            className="btn-top d-inline-flex align-items-center"
          >
            <FaPlus className="me-2" /> Add Invoice
          </Link>
        </Col>
      </Row>

      {/* Stats Summary 
      <Row className="mb-4">
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-success text-white">
            <h4 className="mb-1 me-2">{paidCount}</h4>
            <small>Paid Invoices</small>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-warning text-dark">
            <h4 className="mb-1 me-2">{pendingCount}</h4>
            <small>Pending</small>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-danger text-white">
            <h4 className="mb-1 me-2">{overdueCount}</h4>
            <small>Overdue</small>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card text-center p-3 border rounded bg-primary text-white">
            <h4 className="mb-1 me-2">{invoices.length}</h4>
            <small>Total Invoices</small>
          </div>
        </Col>
      </Row>
      */}

      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading invoices...</p>
        </div>
      ) : (
        <>
          {/* Table Section */}
          <div className="table-pagination-wrapper">
            <div className="table-pagination-content">
              <Table bordered responsive className="table-striped customer-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Invoice ID</th>
                    <th>Date</th>
                    <th>Customer Name</th>
                    <th>Mobile</th>
                    <th>Amount</th>
                    {/* <th>Status</th> */}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentInvoices.map((invoice, index) => (
                    <tr key={invoice.id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaFileInvoice className="text-primary me-2" />
                          {invoice.invoice_no || `${invoice.id}`}
                        </div>
                      </td>
                      <td>{invoice.date}</td>
                      <td>{invoice.customer_name || "N/A"}</td>
                      <td>{invoice.customer_phone || "N/A"}</td>
                      <td className="fw-bold right-align">{formatCurrency(invoice.grand_total)}</td>
                      {/* <td>
                        <span className={getStatusClass(invoice.status)}>
                          {invoice.status || "Generated"}
                        </span>
                      </td> */}
                      <td>
                        <div className="action-icons">
                          <Link
                            to={`/viewinvoice/${invoice.id}`}
                            className="view-link"
                            title="View Invoice"
                          >
                            <FaEye className="view-icon" />
                          </Link>

                          {/* <Link
                            to={`/editinvoice/${invoice.id}`}
                            className="me-2"
                            title="Edit Invoice"
                          >
                            <FaEdit className="edit-icon" />
                          </Link> */}
                          <FaTrash
                            className="delete-icon"
                            title="Delete Invoice"
                            onClick={() => handleDelete(invoice.id)}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentInvoices.length === 0 && !loading && (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        <div className="text-muted">
                          <FaFileInvoice size={48} className="mb-3" />
                          <p>No invoices found</p>
                          <small>Try adjusting your search or filters</small>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}

          {/* Summary Footer */}
          <Row className="mt-4">
            <Col xs={12}>
              <div className="summary-footer p-3 border rounded bg-light">
                <Row>
                  <Col md={4}>
                    <strong>Total Invoices:</strong> {filteredInvoices.length}
                  </Col>
                  <Col md={4}>
                    <strong>Total Amount:</strong> {formatCurrency(totalAmount)}
                  </Col>
                  <Col md={4}>
                    <strong>Filtered Results:</strong> {filteredInvoices.length} of {invoices.length}
                  </Col>
                  {/* <Col md={3}>
                    <strong>Package Charges Total:</strong> {formatCurrency(
                      filteredInvoices.reduce((total, invoice) => 
                        total + (parseFloat(invoice.package_charges) || 0), 0)
                    )}
                  </Col> */}
                </Row>
              </div>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default InvoiceList;